import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight } from '../components/Icons';
import { services } from '../data/services';
import useSwipe from '../hooks/useSwipe';
import { schemas, getServiceMeta } from '../config/seo';
import ResponsiveImage from '../components/ResponsiveImage';

const ServicePage = () => {
    const { slug } = useParams();
    const [imageIndex, setImageIndex] = useState(0);

    // Find service by slug/id
    const service = services.find(s => s.id === slug);
    const serviceIndex = services.findIndex(s => s.id === slug);

    // Get prev/next services
    const prevService = serviceIndex > 0 ? services[serviceIndex - 1] : null;
    const nextService = serviceIndex < services.length - 1 ? services[serviceIndex + 1] : null;

    // Swipe handlers
    const nextImage = () => {
        if (service?.images?.length > 1) {
            setImageIndex((prev) => (prev + 1) % service.images.length);
        }
    };

    const prevImage = () => {
        if (service?.images?.length > 1) {
            setImageIndex((prev) => (prev - 1 + service.images.length) % service.images.length);
        }
    };

    // Swipe handlers - pass functions directly (not as object)
    const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(nextImage, prevImage);

    // Reset image index when service changes
    useEffect(() => {
        setImageIndex(0);
        window.scrollTo(0, 0);
    }, [slug]);

    // Redirect if service not found
    if (!service) {
        return (
            <div className="service-page">
                <div className="container section-padding">
                    <h1>Service Not Found</h1>
                    <p>The service you're looking for doesn't exist.</p>
                    <Link to="/#services" className="btn-primary-custom">
                        View All Services
                    </Link>
                </div>
            </div>
        );
    }

    const meta = getServiceMeta(service, slug);

    return (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <link rel="canonical" href={meta.url} />

                <meta property="og:title" content={meta.title} />
                <meta property="og:description" content={meta.description} />
                <meta property="og:image" content={meta.image} />
                <meta property="og:url" content={meta.url} />
                <meta property="og:type" content={meta.type} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={meta.title} />
                <meta name="twitter:description" content={meta.description} />
                <meta name="twitter:image" content={meta.image} />

                <script type="application/ld+json">
                    {JSON.stringify(schemas.service(service))}
                </script>
            </Helmet>

            <div className="service-page">
                {/* Hero Section */}
                <section className="service-page-hero">
                    <div
                        className="page-gallery"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        <div
                            className="page-gallery-track"
                            style={{ transform: `translateX(-${imageIndex * 100}%)` }}
                        >
                            {service.images.map((img, idx) => (
                                <div className="page-gallery-slide" key={idx}>
                                    <ResponsiveImage
                                        src={img}
                                        alt={`${service.title} - ${service.category} - Image ${idx + 1}`}
                                        loading={idx === 0 ? "eager" : "lazy"}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="page-gallery-overlay"></div>

                        {/* Title Overlay - Center Top */}
                        <div className="page-hero-title-overlay">
                            <div className="container">
                                <h1 className="page-hero-title">{service.title}</h1>
                            </div>
                        </div>

                        {/* Gallery Navigation Arrows */}
                        {service.images.length > 1 && (
                            <>
                                <button
                                    className="page-gallery-nav prev"
                                    onClick={prevImage}
                                    aria-label="Previous image"
                                >
                                    <ChevronLeft />
                                </button>
                                <button
                                    className="page-gallery-nav next"
                                    onClick={nextImage}
                                    aria-label="Next image"
                                >
                                    <ChevronRight />
                                </button>
                                <div className="page-gallery-dots">
                                    {service.images.map((_, idx) => (
                                        <button
                                            key={idx}
                                            className={`page-gallery-dot ${idx === imageIndex ? 'active' : ''}`}
                                            onClick={() => setImageIndex(idx)}
                                            aria-label={`Go to image ${idx + 1}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Info Bar - Under Gallery */}
                    <div className="page-info-bar">
                        <div className="container">
                            <span className="page-category">{service.category}</span>
                            <Link to="/#services" className="page-nav-link">
                                All Services <ChevronRight />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="service-page-content section-padding">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-7">
                                <h2 className="page-section-title">Overview</h2>
                                <div className="divider"></div>
                                {Array.isArray(service.fullDescription) ?
                                    service.fullDescription.map((paragraph, idx) => (
                                        <p key={idx} className="page-text">{paragraph}</p>
                                    ))
                                    :
                                    <p className="page-text">{service.fullDescription}</p>
                                }
                            </div>
                            <div className="col-lg-4 offset-lg-1">
                                <div className="page-sidebar">
                                    <h3 className="page-sidebar-title">What We Offer</h3>
                                    <ul className="page-sidebar-list">
                                        {service.offerings.map((offering, idx) => (
                                            <li key={idx}>{offering}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                {service.stats && (
                    <section className="service-page-features">
                        <div className="container">
                            <div className="row">
                                {service.stats.map((stat, idx) => (
                                    <div className="col-4" key={idx}>
                                        <div className="service-page-feature">
                                            <span className="service-page-feature-number">{stat.number}</span>
                                            <p className="service-page-feature-text">{stat.label}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* CTA Section */}
                <section className="page-cta section-padding">
                    <div className="container text-center">
                        <h2 className="page-cta-title">Ready to Start Your Project?</h2>
                        <p className="page-cta-text">
                            Let's talk about your building's needs.
                        </p>
                        <Link to="/#contact" className="btn-primary-custom">
                            Request a Consultation
                        </Link>
                    </div>
                </section>

                {/* Navigation to Other Services */}
                <section className="page-bottom-nav">
                    <div className="container">
                        <div className="row">
                            <div className="col-6">
                                {prevService && (
                                    <Link to={`/services/${prevService.id}`} className="page-bottom-nav-link prev">
                                        <span className="page-bottom-nav-label">
                                            <ChevronLeft /> Previous Service
                                        </span>
                                        <span className="page-bottom-nav-title">{prevService.title}</span>
                                    </Link>
                                )}
                            </div>
                            <div className="col-6 text-end">
                                {nextService && (
                                    <Link to={`/services/${nextService.id}`} className="page-bottom-nav-link next">
                                        <span className="page-bottom-nav-label">
                                            Next Service <ChevronRight />
                                        </span>
                                        <span className="page-bottom-nav-title">{nextService.title}</span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ServicePage;
